import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import usePush from '../hooks/usePush';
import useShortCode from '../hooks/useShortCode';
import useUser from '../hooks/useUser';
import useDisplayBanner from '../hooks/useDisplayBanner';
import FeedbackService from '../services/feedback-service';
import { pendo } from '../util/logging';
import style from './SurveyPageShared.module.scss';

const PILLARS = {
  receivables: {
    title: 'Receivables feedback',
    backHref: '/receivables',
    section: 'receivables',
    intro: "Tell us what's working and what isn't. Your feedback goes directly to our product team.",
    workingWellLabel: "What's working well in Receivables?",
    improveLabel: 'What would you improve about Receivables?',
  },
  cashflow: {
    title: 'Cash Flow feedback',
    backHref: '/cashflow',
    section: 'cash_flow',
    intro: "Tell us what's working and what isn't. Your feedback goes directly to our product team.",
    workingWellLabel: "What's working well in Cash Flow?",
    improveLabel: 'What would you improve about Cash Flow?',
  },
};

function FeedbackPage() {
  const location = useLocation();
  const showBanner = useDisplayBanner();
  const shortCode = useShortCode();
  const user = useUser();
  const pillarKey = useMemo(
    () => (location.pathname.startsWith('/cashflow') ? 'cashflow' : 'receivables'),
    [location.pathname],
  );
  const pillar = PILLARS[pillarKey];

  const [workingWell, setWorkingWell] = useState('');
  const [improve, setImprove] = useState('');
  const [isBug, setIsBug] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { execute: submitFeedback, loading } = usePush(FeedbackService.submit, {
    errorToast: true,
  });

  const canSubmit = Boolean(workingWell.trim() || improve.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const signals = [
      workingWell ? `Working well: ${workingWell}` : '',
      improve ? `Improve: ${improve}` : '',
    ].filter(Boolean).join('\n\n');

    const userId = user.userId || user._id || null;

    const [err] = await submitFeedback({
      shortCode,
      section: pillar.section,
      working_well: workingWell,
      improve,
      is_bug_report: isBug,
      user_id: userId,
      signals,
    });

    if (err) return;

    pendo.track('give_feedback_submitted', {
      section: pillar.section,
      user_id: userId,
      is_bug_report: isBug,
    });

    setSubmitted(true);
  };

  return (
    <div className={`${style.page} ${showBanner ? style.pageWithBanner : ''}`}>
      <h1 className={style.heading}>{pillar.title}</h1>
      <p className={style.intro}>
        {pillar.intro}
      </p>

      <div className={style.panel}>
        {submitted ? (
          <>
            <h2 className={style.doneTitle}>Thanks for your feedback</h2>
            <p className={style.doneText}>Your response has been captured.</p>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <div className={style.field}>
              <label className={style.label}>{pillar.workingWellLabel}</label>
              <textarea
                className={style.textarea}
                value={workingWell}
                onChange={(e) => setWorkingWell(e.target.value)}
                placeholder="Share what is helping your workflow..."
              />
            </div>

            <div className={style.field}>
              <label className={style.label}>{pillar.improveLabel}</label>
              <textarea
                className={style.textarea}
                value={improve}
                onChange={(e) => setImprove(e.target.value)}
                placeholder="Share friction points or missing capabilities..."
              />
            </div>

            <div className={style.controlsRow}>
              <label className={style.toggleRow}>
                <input
                  type="checkbox"
                  checked={isBug}
                  onChange={(e) => setIsBug(e.target.checked)}
                  className={style.switchInput}
                />
                <span
                  className={`${style.switchTrack} ${isBug ? style.switchTrackOn : ''}`}
                  aria-hidden="true"
                >
                  <span className={`${style.switchThumb} ${isBug ? style.switchThumbOn : ''}`} />
                </span>
                <span>Is this a bug report?</span>
              </label>

              <button
                type="submit"
                className={`${style.submit} ${canSubmit && !loading ? '' : style.submitDisabled}`}
                disabled={!canSubmit || loading}
              >
                Submit feedback
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default FeedbackPage;
